import { useEffect, useState } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import EmailVerificationModal from './EmailVerificationModal'

interface Props {
  user: User
  signOut: () => Promise<void> | void
  children: React.ReactNode
}

/**
 * Wraps the entire authenticated app (onboarding + dashboard). If the
 * authenticated user has never verified their email via the OTP flow, blocks
 * all underlying UI behind the verification modal.
 *
 * Verification status lives on auth.users.user_metadata.email_verified_via_otp,
 * which the worker sets via the Supabase admin API on successful verify.
 *
 * The gate accepts user/signOut as props (rather than calling useAuth itself)
 * so it shares state with App.tsx — a second useAuth instance here would
 * bootstrap independently, briefly seeing user=null and flashing children
 * before the verification check could run.
 */
export default function EmailVerificationGate({ user, signOut, children }: Props) {
  // Seed verified synchronously from local user_metadata. For a freshly
  // signed-up user this is undefined → false, so we render the modal on the
  // very first paint without any null/loading frame.
  const [verified, setVerified] = useState<boolean>(
    Boolean(user.user_metadata?.email_verified_via_otp)
  )

  // After mount, pull a fresh user record in case the worker updated metadata
  // server-side but the local JWT hasn't refreshed yet.
  useEffect(() => {
    if (verified) return
    let cancelled = false
    void (async () => {
      const { data } = await supabase.auth.getUser()
      const fresh = data.user?.user_metadata?.email_verified_via_otp
      if (!cancelled && fresh) setVerified(true)
    })()
    return () => { cancelled = true }
  }, [user.id, verified])

  const handleVerified = async () => {
    await supabase.auth.refreshSession()
    setVerified(true)
  }

  if (verified) return <>{children}</>

  return (
    <EmailVerificationModal
      email={user.email || ''}
      autoSendOnOpen
      onVerified={handleVerified}
      onSignOut={() => { void signOut() }}
    />
  )
}
